/**
 * GraphiQL against platform-service-main, pointed at mechanicus's own
 * `/api/graphql` route rather than at ps-main directly. The route attaches the
 * caller's credential server-side (see lib/psMain.ts), so the browser never
 * holds a ps-main token, and ps-main never has to expose GraphiQL itself.
 *
 * Queries and mutations both run. The route logs every operation.
 */

// The explorer plugin wraps `graphiql-explorer`, which is CommonJS and reaches
// `graphql` through `require`. Everything else here imports it as ESM, and
// `graphql` 16 ships both formats (`main: index`, `module: index.mjs`), so a
// bundler pulls in two copies of the library. Two copies means two sets of
// class identities, and the explorer dies on the first `instanceof` check with
// "Cannot use GraphQLNonNull ... from another module or realm". So
// patches/graphql@16.14.2.patch points `main` at the ESM build too, which
// leaves exactly one copy in the bundle.
import { explorerPlugin } from "@graphiql/plugin-explorer";
import "@graphiql/plugin-explorer/style.css";
import { GraphiQL } from "graphiql";
// ponytail: App.tsx loads this module lazily, which keeps GraphiQL and Monaco
// out of the entry JS, but Bun hoists a lazy chunk's CSS into the entry
// stylesheet anyway — about 880 KB that every page load pays for. If that
// starts to matter, serve these two files from node_modules and inject a
// <link> here, the same way index.ts serves the Monaco workers.
import "graphiql/style.css";
import { runGraphql } from "../lib/client";

// Monaco reads this global when it starts a language service, so it has to be
// set before GraphiQL mounts. The three worker bundles come from this app's
// own origin — see `serveMonacoWorker` in index.ts for why they are built
// there instead of by the frontend bundler. Cast because `MonacoEnvironment`
// is monaco-editor's own global, which mechanicus does not import directly.
(
  globalThis as unknown as {
    MonacoEnvironment: { getWorker(id: string, label: string): Worker };
  }
).MonacoEnvironment = {
  getWorker(_id, label) {
    const name = label === "json" || label === "graphql" ? label : "editor";
    return new Worker(`/monaco-workers/${name}`, { type: "module" });
  },
};

// Created once: a new plugin array on every render remounts the sidebar.
const PLUGINS = [explorerPlugin()];

const DEFAULT_QUERY = `# GraphQL console for platform-service-main.
#
# Runs as you, with your own permissions. Mutations are allowed, and every
# operation is logged with your user id.
#
# Use the Explorer in the left sidebar to build a query by clicking fields,
# or the Docs tab to read the schema.

query {
  accounts {
    identity {
      isSuperuser
    }
  }
}
`;

export function GraphqlConsole() {
  return (
    <div className="graphql-console">
      <GraphiQL
        fetcher={runGraphql}
        plugins={PLUGINS}
        defaultQuery={DEFAULT_QUERY}
        // The rest of mechanicus is dark-only, so do not let GraphiQL's own
        // theme toggle put a light panel in the middle of a dark page.
        forcedTheme="dark"
      />
    </div>
  );
}
